import * as React from "react";
import { isEqual, uniq } from "lodash";

import {
  IXFooterProps,
  XFooterProps,
} from "../../../utils/interfaces/common/XFooter.interfaces";
import { getValueOnBreakpoint } from "../../../lib/getValueOnBreakpoint";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { CONTAINERS_NAME } from "../../../utils/constants/common/componentName";
import { useXCommonUpdater } from "../../../hooks/useXCommon";
import XFooterCore from "./core";

import XStack from "../../containers/XStack/prod";
import XGrid from "../../containers/XGrid/prod";
import { TDynamicXElement } from "../../../utils/types/core/DynamicXElement";

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XFooter: React.FC<XFooterProps> = React.memo(
  ({ initData = { all: {} } }) => {
    // WE TRACK THE BREAK POINTS CHANGES
    const breakpoint = useBreakpoint();

    // WE NOW USE THE CURRENT BREAKPOINT VALUE ON THIS ELEMENT
    const valueAtBreakPoint = getValueOnBreakpoint<IXFooterProps>(
      initData,
      breakpoint
    );

    return (
      <XFooterCore
        index="FOOTER"
        props={valueAtBreakPoint}
        DynamicXElement={DynamicXElement}
      />
    );
  },
  compareProps
);

export default XFooter;

const DynamicXElement: TDynamicXElement = React.memo(
  ({ child, index, position }) => {
    const setParent = useXCommonUpdater(child.config.childOf ?? "NO_PARENT");

    const setFooter = useXCommonUpdater(index);

    React.useEffect(() => {
      setParent((old) => {
        if (!old) return null;

        const elementsIndexes = old.elementsIndexes
          ? [...old.elementsIndexes, index]
          : [index];

        return { ...old, elementsIndexes: uniq(elementsIndexes) };
      });

      setFooter({
        name: child.name,

        index: child.index ?? index,

        hierachy: child.hierachy ?? "XFooter-" + child.name,

        value: child.value ?? child.props ?? { all: {} },

        elementsIndexes: child.elementsIndexes ?? [],

        hasChildren: child.hasChildren ?? CONTAINERS_NAME.includes(child.name),

        childOf: child.childOf ?? child.config.childOf ?? null,
      });
    }, [index, position]);

    if (child.config.childOf !== "FOOTER") return null;

    switch (child.name) {
      case "XStack":
        return <XStack {...child} />;
      case "XGrid":
        return <XGrid {...child} />;

      default:
        return null;
    }
  },
  compareProps
);
